/**
 * AdminBusinessDetail Component
 * Full detail page for viewing and editing a business/salon
 * URL: /beu-admin-x7k9/businesses/:id
 */

import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import PlacesAutocomplete, {
    geocodeByAddress,
    getLatLng,
} from 'react-places-autocomplete';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminBusinessDetail extends Component {
    constructor(props) {
        super(props);
        this.state = {
            business: null,
            loading: true,
            error: null,
            isEditing: false,
            editForm: {},
            saving: false,
            saveSuccess: false,
            saveError: null,
            // Opening hours
            openingHours: [
                { dayName: 'Monday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Tuesday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Wednesday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Thursday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Friday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Saturday', sTime: '09:00', eTime: '17:00', isClose: false },
                { dayName: 'Sunday', sTime: '09:00', eTime: '17:00', isClose: true }
            ],
            openingHoursSaving: false,
            // Gallery
            galleryImages: [],
            galleryUploading: false,
            // Client Upload
            showClientUploadModal: false,
            clientUploadFile: null,
            clientUploadHairdresserId: '',
            clientUploading: false,
            clientUploadError: null,
            clientUploadSuccess: null,
            salonHairdressers: []
        };
        this.clientFileInputRef = React.createRef();
    }

    componentDidMount() {
        this.fetchBusiness();
    }

    fetchBusiness = async () => {
        const { id } = this.props.match.params;
        this.setState({ loading: true, error: null });

        try {
            const response = await adminService.getSalonById(id);
            if (response.statusCode === 1) {
                const business = response.responseData;

                // Get gallery images, fallback to main image if no gallery
                let galleryImages = business.galleryImages || [];
                // Filter out any empty URLs
                galleryImages = galleryImages.filter(img => img && img.url);
                // If no gallery images, try to use the main image/imageUrl
                if (galleryImages.length === 0 && (business.imageUrl || business.image)) {
                    galleryImages = [{
                        url: business.imageUrl || business.image,
                        caption: '',
                        order: 0
                    }];
                }

                this.setState({
                    business,
                    editForm: {
                        salonName: business.salonName || business.name || '',
                        email: business.email || '',
                        phoneNumber: business.phoneNumber || '',
                        isShow: business.isShow !== false,
                        status: business.status || 1,
                        // Additional fields
                        businessType: business.businessType || 'hair_salon',
                        bio: business.bio || '',
                        formattedAddress: business.formattedAddress || '',
                        latitude: business.latitude || (business.loc && business.loc[1]) || '',
                        longitude: business.longitude || (business.loc && business.loc[0]) || '',
                        countryShortName: business.countryShortName || '',
                        countryLongName: business.countryLongName || '',
                        postalCode: business.postalCode || '',
                        administrativeAreaLevel1: business.administrativeAreaLevel1 || '',
                        administrativeAreaLevel2: business.administrativeAreaLevel2 || '',
                        onlineBookingContactPhone: business.onlineBookingContactPhone || '',
                        onlineBookingContactEmail: business.onlineBookingContactEmail || ''
                    },
                    openingHours: business.opening_hours || this.state.openingHours,
                    galleryImages,
                    loading: false
                });
            } else {
                this.setState({ error: 'Failed to load business', loading: false });
            }
        } catch (error) {
            console.error('Error fetching business:', error);
            this.setState({ error: 'Failed to load business', loading: false });
        }
    }

    handleEditFormChange = (field, value) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                [field]: value
            },
            saveSuccess: false,
            saveError: null
        }));
    }

    handleSave = async () => {
        const { business, editForm } = this.state;
        this.setState({ saving: true, saveError: null, saveSuccess: false });

        try {
            const response = await adminService.updateSalon(business._id, editForm);
            if (response.statusCode === 1) {
                this.setState({
                    business: response.responseData,
                    saving: false,
                    isEditing: false,
                    saveSuccess: true
                });
                setTimeout(() => this.setState({ saveSuccess: false }), 3000);
            } else {
                this.setState({ saveError: 'Failed to save changes', saving: false });
            }
        } catch (error) {
            console.error('Error saving business:', error);
            this.setState({ saveError: 'Failed to save changes', saving: false });
        }
    }

    handleDelete = async () => {
        const { business } = this.state;
        const name = business.salonName || business.name;
        if (!window.confirm(`Are you sure you want to delete "${name}"?\n\nThis will affect all associated professionals and bookings. This action cannot be undone.`)) {
            return;
        }

        try {
            const response = await adminService.deleteSalon(business._id);
            if (response.statusCode === 1) {
                this.props.history.push('/beu-admin-x7k9/businesses');
            } else {
                alert('Failed to delete business');
            }
        } catch (error) {
            console.error('Error deleting business:', error);
            alert('Failed to delete business');
        }
    }

    cancelEdit = () => {
        const { business } = this.state;
        this.setState({
            isEditing: false,
            editForm: {
                salonName: business.salonName || business.name || '',
                email: business.email || '',
                phoneNumber: business.phoneNumber || '',
                isShow: business.isShow !== false,
                status: business.status || 1,
                businessType: business.businessType || 'hair_salon',
                bio: business.bio || '',
                formattedAddress: business.formattedAddress || '',
                latitude: business.latitude || (business.loc && business.loc[1]) || '',
                longitude: business.longitude || (business.loc && business.loc[0]) || '',
                countryShortName: business.countryShortName || '',
                countryLongName: business.countryLongName || '',
                postalCode: business.postalCode || '',
                administrativeAreaLevel1: business.administrativeAreaLevel1 || '',
                administrativeAreaLevel2: business.administrativeAreaLevel2 || '',
                onlineBookingContactPhone: business.onlineBookingContactPhone || '',
                onlineBookingContactEmail: business.onlineBookingContactEmail || ''
            },
            saveError: null
        });
    }

    // Location handling for Google Places Autocomplete
    handleAddressChange = (formattedAddress) => {
        this.setState(prevState => ({
            editForm: {
                ...prevState.editForm,
                formattedAddress
            },
            saveSuccess: false,
            saveError: null
        }));
    }

    handleAddressSelect = (address) => {
        geocodeByAddress(address)
            .then(results => {
                let countryShortName = '';
                let countryLongName = '';
                let postalCode = '';
                let administrativeAreaLevel1 = '';
                let administrativeAreaLevel2 = '';

                const address_components = results[0].address_components;
                for (let i = 0; i < address_components.length; i++) {
                    const component = address_components[i];
                    if (component['types'][0]) {
                        if (component['types'][0] === 'administrative_area_level_2') {
                            administrativeAreaLevel2 = component['long_name'];
                        }
                        if (component['types'][0] === 'administrative_area_level_1') {
                            administrativeAreaLevel1 = component['short_name'];
                        }
                        if (component['types'][0] === 'postal_code') {
                            postalCode = component['long_name'];
                        }
                        if (component['types'][0] === 'country') {
                            countryShortName = component['short_name'];
                            countryLongName = component['long_name'];
                        }
                    }
                }

                this.setState(prevState => ({
                    editForm: {
                        ...prevState.editForm,
                        formattedAddress: results[0].formatted_address,
                        countryShortName,
                        countryLongName,
                        postalCode,
                        administrativeAreaLevel1,
                        administrativeAreaLevel2
                    }
                }));

                return getLatLng(results[0]);
            })
            .then(latLng => {
                this.setState(prevState => ({
                    editForm: {
                        ...prevState.editForm,
                        latitude: latLng.lat,
                        longitude: latLng.lng
                    }
                }));
            })
            .catch(error => console.error('Error geocoding address:', error));
    }

    // Opening hours handlers
    handleDayToggle = (dayIndex) => {
        const openingHours = [...this.state.openingHours];
        openingHours[dayIndex] = {
            ...openingHours[dayIndex],
            isClose: !openingHours[dayIndex].isClose
        };
        this.setState({ openingHours }, this.saveOpeningHours);
    }

    handleTimeChange = (dayIndex, field, value) => {
        const openingHours = [...this.state.openingHours];
        openingHours[dayIndex] = {
            ...openingHours[dayIndex],
            [field]: value
        };
        this.setState({ openingHours });
    }

    handleTimeBlur = () => {
        this.saveOpeningHours();
    }

    saveOpeningHours = async () => {
        const { business, openingHours } = this.state;
        if (!business || !business._id) return;

        try {
            await adminService.updateSalon(business._id, { opening_hours: openingHours });
        } catch (error) {
            console.error('Error saving opening hours:', error);
        }
    }

    // Gallery image handlers
    handleGalleryImageUpload = async (e) => {
        const file = e.target.files[0];
        const inputElement = e.target;
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
            return;
        }

        this.setState({ galleryUploading: true });

        try {
            const { business } = this.state;
            const response = await adminService.uploadSalonGalleryImage(business._id, file);

            if (response.statusCode === 1 && response.responseData) {
                const newImage = response.responseData;
                this.setState(prevState => ({
                    galleryImages: [...prevState.galleryImages, newImage],
                    galleryUploading: false
                }));
            } else {
                alert('Failed to upload image');
                this.setState({ galleryUploading: false });
            }
        } catch (error) {
            console.error('Error uploading gallery image:', error);
            alert('Failed to upload image');
            this.setState({ galleryUploading: false });
        }

        if (inputElement) {
            inputElement.value = '';
        }
    }

    handleDeleteGalleryImage = async (imageUrl) => {
        if (!window.confirm('Are you sure you want to delete this image?')) {
            return;
        }

        try {
            const { business } = this.state;
            const response = await adminService.deleteSalonGalleryImage(business._id, imageUrl);

            if (response.statusCode === 1) {
                this.setState(prevState => ({
                    galleryImages: prevState.galleryImages.filter(img => img.url !== imageUrl)
                }));
            } else {
                alert('Failed to delete image');
            }
        } catch (error) {
            console.error('Error deleting gallery image:', error);
            alert('Failed to delete image');
        }
    }

    handleMakeHeadlineImage = async (index) => {
        const images = [...this.state.galleryImages];
        const [movedImage] = images.splice(index, 1);
        images.unshift(movedImage);

        images.forEach((img, i) => {
            img.order = i;
        });

        this.setState({ galleryImages: images });

        try {
            const { business } = this.state;
            const reorderData = images.map((img, i) => ({
                url: img.url,
                order: i
            }));
            await adminService.reorderSalonGalleryImages(business._id, reorderData);
        } catch (error) {
            console.error('Error reordering gallery images:', error);
            alert('Failed to save image order');
        }
    }

    // Client Upload handlers
    openClientUploadModal = async () => {
        const { business } = this.state;
        this.setState({
            showClientUploadModal: true,
            clientUploadFile: null,
            clientUploadHairdresserId: '',
            clientUploadError: null,
            clientUploadSuccess: null,
            salonHairdressers: [] // Reset while loading
        });
        
        try {
            const response = await adminService.getHairdressers({
                limit: 1000,
                salonId: business._id
            });

            if (response.statusCode === 1) {
                this.setState({
                    salonHairdressers: response.responseData?.hairdressers || []
                });
            } else {
                this.setState({ clientUploadError: 'Failed to load professionals' });
            }
        } catch (error) {
            console.error('Error fetching hairdressers:', error);
            this.setState({ clientUploadError: 'Error loading professionals: ' + error.message });
        }
    }

    closeClientUploadModal = () => {
        this.setState({
            showClientUploadModal: false,
            clientUploadFile: null,
            clientUploadHairdresserId: '',
            clientUploading: false,
            clientUploadError: null,
            clientUploadSuccess: null
        });
        if (this.clientFileInputRef.current) {
            this.clientFileInputRef.current.value = '';
        }
    }

    handleClientFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = [
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel'
            ];
            if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
                this.setState({ clientUploadError: 'Please select a valid Excel file (.xlsx or .xls)' });
                return;
            }
            this.setState({ clientUploadFile: file, clientUploadError: null });
        }
    }

    handleUploadClients = async () => {
        const { clientUploadFile, clientUploadHairdresserId } = this.state;

        if (!clientUploadFile) {
            this.setState({ clientUploadError: 'Please select a file to upload' });
            return;
        }

        if (!clientUploadHairdresserId) {
            this.setState({ clientUploadError: 'Please select a professional' });
            return;
        }

        this.setState({ clientUploading: true, clientUploadError: null, clientUploadSuccess: null });

        try {
            const response = await adminService.uploadClientList(clientUploadHairdresserId, clientUploadFile);
            if (response.statusCode === 1) {
                this.setState({
                    clientUploadSuccess: 'Clients uploaded successfully!',
                    clientUploading: false,
                    clientUploadFile: null
                });
                if (this.clientFileInputRef.current) {
                    this.clientFileInputRef.current.value = '';
                }
            } else {
                this.setState({
                    clientUploadError: response.message || 'Failed to upload clients',
                    clientUploading: false
                });
            }
        } catch (error) {
            console.error('Error uploading clients:', error);
            this.setState({
                clientUploadError: error.message || 'Failed to upload clients',
                clientUploading: false
            });
        }
    }

    renderClientUploadModal() {
        const { showClientUploadModal, clientUploadFile, clientUploadHairdresserId, clientUploading, clientUploadError, clientUploadSuccess, salonHairdressers } = this.state;

        if (!showClientUploadModal) return null;

        return (
            <div className="admin-modal-overlay" onClick={this.closeClientUploadModal}>
                <div className="admin-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                    <div className="admin-modal-header">
                        <h2>Upload Client List</h2>
                        <button className="admin-modal-close" onClick={this.closeClientUploadModal}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="24" height="24">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                    </div>

                    <div className="admin-modal-body">
                        <div className="admin-form">
                            <div className="admin-form-group">
                                <label>Select Professional *</label>
                                <select
                                    value={clientUploadHairdresserId}
                                    onChange={(e) => this.setState({ clientUploadHairdresserId: e.target.value })}
                                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                >
                                    <option value="">
                                        {salonHairdressers.length === 0
                                            ? '-- No professionals registered to this salon --'
                                            : '-- Select a professional --'}
                                    </option>
                                    {salonHairdressers.map(h => (
                                        <option key={h._id} value={h.userId}>
                                            {h.userName || h.name || `${h.first_name || ''} ${h.last_name || ''}`.trim() || h.userEmail || h.email || 'Unknown'}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="admin-form-group">
                                <label>Excel File (.xlsx) *</label>
                                <input
                                    type="file"
                                    ref={this.clientFileInputRef}
                                    accept=".xlsx,.xls"
                                    onChange={this.handleClientFileChange}
                                    style={{ width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #ddd' }}
                                />
                                {clientUploadFile && (
                                    <p style={{ marginTop: '8px', color: '#666', fontSize: '14px' }}>
                                        Selected: {clientUploadFile.name}
                                    </p>
                                )}
                            </div>

                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '16px',
                                borderRadius: '8px',
                                marginTop: '16px',
                                border: '1px solid #e9ecef'
                            }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600' }}>
                                    Excel File Format
                                </h4>
                                <p style={{ margin: '0', fontSize: '13px', color: '#666' }}>
                                    Your Excel file should have the following columns:
                                </p>
                                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px', color: '#666' }}>
                                    <li><strong>Column A:</strong> Name (required)</li>
                                    <li><strong>Column B:</strong> Email</li>
                                    <li><strong>Column C:</strong> Phone Number</li>
                                    <li><strong>Column D:</strong> Note</li>
                                </ul>
                                <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
                                    Note: First row should be headers and will be skipped.
                                </p>
                            </div>

                            {clientUploadError && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#fff5f5',
                                    border: '1px solid #feb2b2',
                                    borderRadius: '6px',
                                    color: '#c53030'
                                }}>
                                    {clientUploadError}
                                </div>
                            )}

                            {clientUploadSuccess && (
                                <div style={{
                                    marginTop: '16px',
                                    padding: '12px',
                                    backgroundColor: '#f0fff4',
                                    border: '1px solid #9ae6b4',
                                    borderRadius: '6px',
                                    color: '#276749'
                                }}>
                                    {clientUploadSuccess}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="admin-modal-footer">
                        <button
                            className="admin-btn admin-btn-secondary"
                            onClick={this.closeClientUploadModal}
                            disabled={clientUploading}
                        >
                            Cancel
                        </button>
                        <button
                            className="admin-btn admin-btn-primary"
                            onClick={this.handleUploadClients}
                            disabled={clientUploading || !clientUploadFile || !clientUploadHairdresserId}
                        >
                            {clientUploading ? 'Uploading...' : 'Upload Clients'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    getBusinessTypeLabel = (type) => {
        const types = {
            'hair_salon': 'Hair Salon',
            'barber': 'Barber',
            'beauty_salon': 'Beauty Salon',
            'nail_salon': 'Nail Salon',
            'spa': 'Spa',
            'other': 'Other'
        };
        return types[type] || type || 'Not specified';
    }

    getInitials = (name) => {
        if (!name) return 'B';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    }

    formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    getAddress = (business) => {
        if (!business.address) return null;
        const addr = business.address;
        if (typeof addr === 'string') return addr;

        const parts = [];
        if (addr.addressLine1) parts.push(addr.addressLine1);
        if (addr.addressLine2) parts.push(addr.addressLine2);
        if (addr.city) parts.push(addr.city);
        if (addr.postcode) parts.push(addr.postcode);
        if (addr.country) parts.push(typeof addr.country === 'object' ? addr.country.name : addr.country);

        return parts.length > 0 ? parts.join(', ') : null;
    }

    render() {
        const { business, loading, error, isEditing, editForm, saving, saveSuccess, saveError } = this.state;

        if (loading) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                        <p style={{ color: '#666' }}>Loading business...</p>
                    </div>
                </AdminWrapper>
            );
        }

        if (error || !business) {
            return (
                <AdminWrapper>
                    <div style={{ padding: '60px', textAlign: 'center' }}>
                        <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error || 'Business not found'}</p>
                        <Link to="/beu-admin-x7k9/businesses" className="admin-btn admin-btn-secondary">
                            Back to Businesses
                        </Link>
                    </div>
                </AdminWrapper>
            );
        }

        const businessName = business.salonName || business.name || 'Unknown Business';
        const address = this.getAddress(business);

        return (
            <AdminWrapper>
                {/* Breadcrumb */}
                <div style={{ marginBottom: '24px' }}>
                    <Link to="/beu-admin-x7k9/businesses" style={{ color: '#666', textDecoration: 'none', fontSize: '14px' }}>
                        ← Back to Businesses
                    </Link>
                </div>

                {/* Success/Error Messages */}
                {saveSuccess && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#e8f5e9',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#2e7d32',
                        fontWeight: '500'
                    }}>
                        ✓ Changes saved successfully
                    </div>
                )}
                {saveError && (
                    <div style={{
                        padding: '12px 16px',
                        background: '#ffebee',
                        borderRadius: '8px',
                        marginBottom: '24px',
                        color: '#c62828',
                        fontWeight: '500'
                    }}>
                        ✕ {saveError}
                    </div>
                )}

                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: '32px',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                        {business.imageUrl || business.image || business.logo || business.externalImageUrl ? (
                            <img
                                src={business.imageUrl || business.image || business.logo || business.externalImageUrl}
                                alt={businessName}
                                style={{ width: '80px', height: '80px', borderRadius: '12px', objectFit: 'cover' }}
                            />
                        ) : (
                            <div style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '28px',
                                fontWeight: '600'
                            }}>
                                {this.getInitials(businessName)}
                            </div>
                        )}
                        <div>
                            <h1 style={{ margin: '0 0 4px 0', fontSize: '28px', fontWeight: '700' }}>
                                {businessName}
                            </h1>
                            {address && <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>{address}</p>}
                            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                                <span className={`admin-badge ${business.isShow !== false ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                                    {business.isShow !== false ? 'Visible' : 'Hidden'}
                                </span>
                                <span className={`admin-badge ${business.status === 1 ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                    {business.status === 1 ? 'Active' : 'Inactive'}
                                </span>
                                {business.isClaimed === false && (
                                    <span className="admin-badge" style={{ background: '#e3f2fd', color: '#1565c0' }}>
                                        Unclaimed
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        {!isEditing ? (
                            <>
                                <a
                                    href={`/salon/${business.slug || business._id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="admin-btn admin-btn-secondary"
                                >
                                    View Booking Page
                                </a>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => this.setState({ isEditing: true })}
                                >
                                    Edit Business
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="admin-btn admin-btn-secondary"
                                    onClick={this.cancelEdit}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="admin-btn admin-btn-primary"
                                    onClick={this.handleSave}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Content Grid */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
                    {/* Main Content */}
                    <div>
                        {/* Business Images Section */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Business Images</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                                    The first image is the headline image shown on the marketplace.
                                </p>
                                {this.state.galleryImages.length > 0 ? (
                                    <div>
                                        {/* Headline Image */}
                                        <div style={{
                                            position: 'relative',
                                            paddingBottom: '56.25%',
                                            backgroundColor: '#f5f5f5',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            marginBottom: '12px'
                                        }}>
                                            <img
                                                src={this.state.galleryImages[0].url}
                                                alt="Headline"
                                                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                            <span style={{
                                                position: 'absolute',
                                                top: '12px',
                                                left: '12px',
                                                backgroundColor: '#000',
                                                color: '#fff',
                                                padding: '4px 12px',
                                                borderRadius: '4px',
                                                fontSize: '12px',
                                                fontWeight: '500'
                                            }}>
                                                Headline Image
                                            </span>
                                            {isEditing && (
                                                <button
                                                    onClick={() => this.handleDeleteGalleryImage(this.state.galleryImages[0].url)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        width: '28px',
                                                        height: '28px',
                                                        borderRadius: '50%',
                                                        border: 'none',
                                                        backgroundColor: 'rgba(255,255,255,0.95)',
                                                        color: '#dc3545',
                                                        cursor: 'pointer',
                                                        fontSize: '16px'
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                        {/* Other Images Grid */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                            {this.state.galleryImages.slice(1).map((image, index) => (
                                                <div
                                                    key={index + 1}
                                                    style={{
                                                        position: 'relative',
                                                        paddingBottom: '100%',
                                                        backgroundColor: '#f5f5f5',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={image.url}
                                                        alt={`Image ${index + 2}`}
                                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                    {isEditing && (
                                                        <div style={{ position: 'absolute', top: '4px', right: '4px', display: 'flex', gap: '4px' }}>
                                                            <button
                                                                onClick={() => this.handleMakeHeadlineImage(index + 1)}
                                                                style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '50%',
                                                                    border: 'none',
                                                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                                                    cursor: 'pointer',
                                                                    fontSize: '12px'
                                                                }}
                                                                title="Make headline image"
                                                            >
                                                                ★
                                                            </button>
                                                            <button
                                                                onClick={() => this.handleDeleteGalleryImage(image.url)}
                                                                style={{
                                                                    width: '24px',
                                                                    height: '24px',
                                                                    borderRadius: '50%',
                                                                    border: 'none',
                                                                    backgroundColor: 'rgba(255,255,255,0.95)',
                                                                    color: '#dc3545',
                                                                    cursor: 'pointer',
                                                                    fontSize: '14px'
                                                                }}
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {/* Add Image Tile */}
                                            {isEditing && (
                                                <div
                                                    onClick={() => !this.state.galleryUploading && this.galleryInput.click()}
                                                    style={{
                                                        position: 'relative',
                                                        paddingBottom: '100%',
                                                        backgroundColor: '#fff',
                                                        borderRadius: '8px',
                                                        border: '2px dashed #ddd',
                                                        cursor: this.state.galleryUploading ? 'wait' : 'pointer'
                                                    }}
                                                >
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100%',
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                        {this.state.galleryUploading ? (
                                                            <span>Uploading...</span>
                                                        ) : (
                                                            <>
                                                                <span style={{ fontSize: '24px', color: '#aaa' }}>+</span>
                                                                <span style={{ fontSize: '11px', color: '#666' }}>Add Image</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        style={{
                                            padding: '40px 20px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            textAlign: 'center',
                                            border: '2px dashed #ddd',
                                            cursor: isEditing ? 'pointer' : 'default'
                                        }}
                                        onClick={() => isEditing && !this.state.galleryUploading && this.galleryInput.click()}
                                    >
                                        {this.state.galleryUploading ? (
                                            <p style={{ margin: 0, color: '#666' }}>Uploading...</p>
                                        ) : (
                                            <>
                                                <div style={{ fontSize: '32px', color: '#aaa', marginBottom: '8px' }}>📷</div>
                                                <p style={{ margin: 0, color: '#666' }}>No images yet</p>
                                                {isEditing && <p style={{ margin: '8px 0 0', color: '#999', fontSize: '14px' }}>Click to upload</p>}
                                            </>
                                        )}
                                    </div>
                                )}
                                <input
                                    ref={(ref) => this.galleryInput = ref}
                                    type="file"
                                    accept="image/*"
                                    onChange={this.handleGalleryImageUpload}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>

                        {/* Business Details Section */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Business Details</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                {isEditing ? (
                                    <div className="admin-form">
                                        <div className="admin-form-group">
                                            <label>Business Name</label>
                                            <input
                                                type="text"
                                                value={editForm.salonName}
                                                onChange={(e) => this.handleEditFormChange('salonName', e.target.value)}
                                                placeholder="Enter business name"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Business Location</label>
                                            <PlacesAutocomplete
                                                value={editForm.formattedAddress || ''}
                                                onChange={this.handleAddressChange}
                                                onSelect={this.handleAddressSelect}
                                            >
                                                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                                                    <div style={{ position: 'relative' }}>
                                                        <input
                                                            {...getInputProps({
                                                                placeholder: 'Search for address...',
                                                                className: 'admin-form-input'
                                                            })}
                                                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
                                                        />
                                                        {suggestions.length > 0 && (
                                                            <div style={{
                                                                position: 'absolute',
                                                                top: '100%',
                                                                left: 0,
                                                                right: 0,
                                                                background: '#fff',
                                                                border: '1px solid #ddd',
                                                                borderRadius: '8px',
                                                                marginTop: '4px',
                                                                zIndex: 1000,
                                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                                            }}>
                                                                {loading && <div style={{ padding: '10px' }}>Loading...</div>}
                                                                {suggestions.map((suggestion, idx) => (
                                                                    <div
                                                                        key={idx}
                                                                        {...getSuggestionItemProps(suggestion, {
                                                                            style: {
                                                                                padding: '10px 12px',
                                                                                cursor: 'pointer',
                                                                                backgroundColor: suggestion.active ? '#f5f5f5' : '#fff',
                                                                                borderBottom: idx < suggestions.length - 1 ? '1px solid #eee' : 'none'
                                                                            }
                                                                        })}
                                                                    >
                                                                        {suggestion.description}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </PlacesAutocomplete>
                                        </div>
                                        <div className="admin-form-group">
                                            <label>Business Type</label>
                                            <select
                                                value={editForm.businessType}
                                                onChange={(e) => this.handleEditFormChange('businessType', e.target.value)}
                                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
                                            >
                                                <option value="hair_salon">Hair Salon</option>
                                                <option value="barber">Barber</option>
                                                <option value="beauty_salon">Beauty Salon</option>
                                                <option value="nail_salon">Nail Salon</option>
                                                <option value="spa">Spa</option>
                                                <option value="other">Other</option>
                                            </select>
                                        </div>
                                        <div className="admin-form-group">
                                            <label>About Your Business</label>
                                            <textarea
                                                value={editForm.bio}
                                                onChange={(e) => this.handleEditFormChange('bio', e.target.value)}
                                                placeholder="Tell clients about your business..."
                                                rows="4"
                                                style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', resize: 'vertical' }}
                                            />
                                        </div>
                                        <div className="admin-form-row">
                                            <div className="admin-form-group">
                                                <label className="admin-checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={editForm.isShow}
                                                        onChange={(e) => this.handleEditFormChange('isShow', e.target.checked)}
                                                    />
                                                    <span>Visible on Platform</span>
                                                </label>
                                            </div>
                                            <div className="admin-form-group">
                                                <label>Status</label>
                                                <select
                                                    value={editForm.status}
                                                    onChange={(e) => this.handleEditFormChange('status', parseInt(e.target.value))}
                                                >
                                                    <option value={1}>Active</option>
                                                    <option value={0}>Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="admin-detail-grid">
                                        <div className="admin-detail-row">
                                            <label>Business Name</label>
                                            <span>{businessName}</span>
                                        </div>
                                        <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <label>Location</label>
                                            <span>{business.formattedAddress || address || 'N/A'}</span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Business Type</label>
                                            <span>{this.getBusinessTypeLabel(business.businessType)}</span>
                                        </div>
                                        {business.bio && (
                                            <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                                <label>About</label>
                                                <span style={{ whiteSpace: 'pre-wrap' }}>{business.bio}</span>
                                            </div>
                                        )}
                                        {business.website && (
                                            <div className="admin-detail-row">
                                                <label>Website</label>
                                                <a href={business.website} target="_blank" rel="noopener noreferrer" style={{ color: '#4facfe' }}>
                                                    {business.website}
                                                </a>
                                            </div>
                                        )}
                                        <div className="admin-detail-row">
                                            <label>Visibility</label>
                                            <span className={`admin-badge ${business.isShow !== false ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                                                {business.isShow !== false ? 'Visible' : 'Hidden'}
                                            </span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Status</label>
                                            <span className={`admin-badge ${business.status === 1 ? 'admin-badge-success' : 'admin-badge-danger'}`}>
                                                {business.status === 1 ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contact Information Section */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Contact Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                                    These details enable Call and Email action buttons on the online booking page.
                                </p>
                                {isEditing ? (
                                    <div className="admin-form">
                                        <div className="admin-form-group">
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                Phone Number
                                                {editForm.onlineBookingContactPhone && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        background: '#10b981',
                                                        color: '#fff',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        Call button enabled
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type="tel"
                                                value={editForm.onlineBookingContactPhone}
                                                onChange={(e) => this.handleEditFormChange('onlineBookingContactPhone', e.target.value)}
                                                placeholder="e.g. +44 20 1234 5678"
                                            />
                                        </div>
                                        <div className="admin-form-group">
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                Email Address
                                                {editForm.onlineBookingContactEmail && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        background: '#3b82f6',
                                                        color: '#fff',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        Email button enabled
                                                    </span>
                                                )}
                                            </label>
                                            <input
                                                type="email"
                                                value={editForm.onlineBookingContactEmail}
                                                onChange={(e) => this.handleEditFormChange('onlineBookingContactEmail', e.target.value)}
                                                placeholder="e.g. bookings@yoursalon.com"
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="admin-detail-grid">
                                        <div className="admin-detail-row">
                                            <label>Phone Number</label>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {business.onlineBookingContactPhone || business.phoneNumber || 'N/A'}
                                                {business.onlineBookingContactPhone && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        background: '#10b981',
                                                        color: '#fff',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        Call enabled
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <div className="admin-detail-row">
                                            <label>Email Address</label>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                {business.onlineBookingContactEmail || business.email || 'N/A'}
                                                {business.onlineBookingContactEmail && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        background: '#3b82f6',
                                                        color: '#fff',
                                                        padding: '2px 8px',
                                                        borderRadius: '12px'
                                                    }}>
                                                        Email enabled
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Opening Hours Section */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Opening Hours</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {this.state.openingHours.map((day, index) => (
                                        <div
                                            key={day.dayName}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '16px',
                                                padding: '12px',
                                                background: day.isClose ? '#f9f9f9' : '#fff',
                                                borderRadius: '8px',
                                                border: '1px solid #eee'
                                            }}
                                        >
                                            {isEditing && (
                                                <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!day.isClose}
                                                        onChange={() => this.handleDayToggle(index)}
                                                        style={{ opacity: 0, width: 0, height: 0 }}
                                                    />
                                                    <span style={{
                                                        position: 'absolute',
                                                        cursor: 'pointer',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: day.isClose ? '#ccc' : '#4facfe',
                                                        borderRadius: '24px',
                                                        transition: '0.3s'
                                                    }}>
                                                        <span style={{
                                                            position: 'absolute',
                                                            content: '',
                                                            height: '18px',
                                                            width: '18px',
                                                            left: day.isClose ? '3px' : '23px',
                                                            bottom: '3px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            transition: '0.3s'
                                                        }} />
                                                    </span>
                                                </label>
                                            )}
                                            <span style={{ width: '100px', fontWeight: '500', color: day.isClose ? '#999' : '#333' }}>
                                                {day.dayName}
                                            </span>
                                            {day.isClose ? (
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>Closed</span>
                                            ) : (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {isEditing ? (
                                                        <>
                                                            <input
                                                                type="time"
                                                                value={day.sTime}
                                                                onChange={(e) => this.handleTimeChange(index, 'sTime', e.target.value)}
                                                                onBlur={this.handleTimeBlur}
                                                                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                                            />
                                                            <span style={{ color: '#666' }}>to</span>
                                                            <input
                                                                type="time"
                                                                value={day.eTime}
                                                                onChange={(e) => this.handleTimeChange(index, 'eTime', e.target.value)}
                                                                onBlur={this.handleTimeBlur}
                                                                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px' }}
                                                            />
                                                        </>
                                                    ) : (
                                                        <span>{day.sTime} - {day.eTime}</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Owner Info */}
                        {business.owner && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Owner</h3>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                        <div style={{
                                            width: '48px', height: '48px', borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
                                            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '16px', fontWeight: '600'
                                        }}>
                                            {this.getInitials(typeof business.owner === 'object' ? business.owner.name : '')}
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600' }}>
                                                {typeof business.owner === 'object' ? business.owner.name : 'Unknown'}
                                            </div>
                                            <div style={{ color: '#666', fontSize: '14px' }}>
                                                {typeof business.owner === 'object' ? business.owner.email : ''}
                                            </div>
                                        </div>
                                    </div>
                                    {typeof business.owner === 'object' && business.owner._id && (
                                        <Link
                                            to={`/beu-admin-x7k9/users/${business.owner._id}`}
                                            className="admin-btn admin-btn-secondary"
                                            style={{ marginTop: '16px' }}
                                        >
                                            View Owner Profile →
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Staff Count */}
                        {business.hairdresserCount !== undefined && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Staff</h3>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    <div style={{ fontSize: '32px', fontWeight: '700', marginBottom: '8px' }}>
                                        {business.hairdresserCount}
                                    </div>
                                    <div style={{ color: '#666' }}>
                                        {business.hairdresserCount === 1 ? 'Professional' : 'Professionals'} registered
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar */}
                    <div>
                        {/* System Info */}
                        <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">System Information</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div className="admin-detail-grid">
                                    <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                        <label>Business ID</label>
                                        <span className="admin-id-text">{business._id}</span>
                                    </div>
                                    {business.slug && (
                                        <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <label>URL Slug</label>
                                            <span className="admin-id-text">{business.slug}</span>
                                        </div>
                                    )}
                                    <div className="admin-detail-row">
                                        <label>Created</label>
                                        <span>{this.formatDateTime(business.createdTime || business.createdAt)}</span>
                                    </div>
                                    <div className="admin-detail-row">
                                        <label>Last Updated</label>
                                        <span>{this.formatDateTime(business.updatedTime || business.updatedAt)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Google Places Data */}
                        {business.googlePlaceId && (
                            <div className="admin-table-container" style={{ marginBottom: '24px' }}>
                                <div className="admin-table-header">
                                    <h3 className="admin-table-title">Google Places Data</h3>
                                </div>
                                <div style={{ padding: '24px' }}>
                                    <div className="admin-detail-grid">
                                        <div className="admin-detail-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                                            <label>Place ID</label>
                                            <span className="admin-id-text">{business.googlePlaceId}</span>
                                        </div>
                                        {business.googleRating && (
                                            <div className="admin-detail-row">
                                                <label>Google Rating</label>
                                                <span>{business.googleRating} / 5 ({business.googleRatingsTotal || 0} reviews)</span>
                                            </div>
                                        )}
                                        <div className="admin-detail-row">
                                            <label>Status</label>
                                            <span className={`admin-badge ${business.enrichmentStatus === 'enriched' ? 'admin-badge-success' : 'admin-badge-warning'}`}>
                                                {business.enrichmentStatus || 'none'}
                                            </span>
                                        </div>
                                        {business.enrichedAt && (
                                            <div className="admin-detail-row">
                                                <label>Enriched</label>
                                                <span>{this.formatDateTime(business.enrichedAt)}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Quick Actions */}
                        <div className="admin-table-container">
                            <div className="admin-table-header">
                                <h3 className="admin-table-title">Quick Actions</h3>
                            </div>
                            <div style={{ padding: '24px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            const newShow = !(business.isShow !== false);
                                            this.handleEditFormChange('isShow', newShow);
                                            this.setState({ isEditing: false }, this.handleSave);
                                        }}
                                    >
                                        {business.isShow !== false ? 'Hide from Platform' : 'Show on Platform'}
                                    </button>
                                    <button
                                        className="admin-btn admin-btn-secondary"
                                        style={{ width: '100%', justifyContent: 'center' }}
                                        onClick={() => {
                                            const newStatus = business.status === 1 ? 0 : 1;
                                            this.handleEditFormChange('status', newStatus);
                                            this.setState({ isEditing: false }, this.handleSave);
                                        }}
                                    >
                                        {business.status === 1 ? 'Deactivate Business' : 'Activate Business'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Upload Clients Section */}
                <div className="admin-table-container" style={{ marginTop: '48px', border: '1px solid #bae6fd' }}>
                    <div className="admin-table-header" style={{ background: '#f0f9ff' }}>
                        <h3 className="admin-table-title" style={{ color: '#0369a1' }}>Import Clients</h3>
                    </div>
                    <div style={{ padding: '24px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                            <div>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>Upload Client List</div>
                                <div style={{ color: '#666', fontSize: '14px' }}>
                                    Import clients from an Excel file (.xlsx) for a professional in this salon.
                                </div>
                            </div>
                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={this.openClientUploadModal}
                            >
                                Upload Clients
                            </button>
                        </div>
                    </div>
                </div>

                {/* Danger Zone */}
                <div className="admin-danger-zone" style={{ marginTop: '48px' }}>
                    <div className="admin-table-container" style={{ border: '1px solid #ff4444' }}>
                        <div className="admin-table-header" style={{ background: '#fff5f5' }}>
                            <h3 className="admin-table-title" style={{ color: '#cc0000' }}>Danger Zone</h3>
                        </div>
                        <div style={{ padding: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
                                <div>
                                    <div style={{ fontWeight: '600', marginBottom: '4px' }}>Delete this business</div>
                                    <div style={{ color: '#666', fontSize: '14px' }}>
                                        Once deleted, this business and all associated data (services, bookings, professionals) will be permanently removed.
                                    </div>
                                </div>
                                <button
                                    className="admin-btn admin-btn-danger"
                                    onClick={this.handleDelete}
                                >
                                    Delete Business
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Client Upload Modal */}
                {this.renderClientUploadModal()}
            </AdminWrapper>
        );
    }
}

export default AdminBusinessDetail;
